//#region src/module/format/date/index.ts
const toDate = (input) => {
	if (typeof input === "undefined") return;
	if (input === null) return null;
	if (input instanceof Date) return input;
	if (!input) return;
	if (isValidDateString(input)) {
		const [year, month, day] = input.split("-").map((i) => Number.parseInt(i, 10));
		return new Date(year, month - 1, day);
	}
	throw new Error("Invalid date string");
};
/**
* Convert Date or ISOString to date string: yyyy-mm-dd
*
* When passing `Date`: always uses local TZ, not UTC!
*/
function toDateString(input) {
	if (typeof input === "undefined") return;
	if (input === null) return null;
	if (typeof input === "string") {
		if (!input) return;
		if (!(isValidDateString(input) || isValidDateTimeString(input))) throw new Error("Invalid date(time) string");
		return input.substring(0, 10);
	}
	return [
		input.getFullYear(),
		input.getMonth() + 1,
		input.getDate()
	].map((v) => `${v}`.padStart(2, "0")).join("-");
}
function toTimeString(input, precision = "m") {
	if (typeof input === "undefined") return;
	if (input === null) return null;
	if (typeof input === "string") {
		if (!isValidDateTimeString(input)) return;
		switch (precision) {
			case "m": return input.substring(11, 16);
			case "s": return input.substring(11, 19);
			default: return input.substring(11, 23);
		}
	}
	const [h, m, s] = [
		input.getHours(),
		input.getMinutes(),
		input.getSeconds()
	].map((v) => `${v}`.padStart(2, "0"));
	switch (precision) {
		case "m": return `${h}:${m}`;
		case "s": return `${h}:${m}:${s}`;
		default: return `${h}:${m}:${s}.${input.getMilliseconds()}`;
	}
}
/**
* Convert date time strings into a Date object
*
* If date is undefined, will use today
* If time is undefined, will use 00:00
*/
function dateTimeStringsToDate({ date, time, timezone }) {
	if (typeof date === "undefined" && typeof time === "undefined") return;
	const _dateString = date ?? toDateString(/* @__PURE__ */ new Date());
	const _timeString = time ?? "00:00";
	return /* @__PURE__ */ new Date(`${_dateString}T${_timeString}${timezone === "UTC" ? "Z" : ""}`);
}
function isValidDateTimeString(input) {
	if (typeof input === "undefined") return false;
	let [date, time] = input.split("T");
	time = time?.replace(/(Z)|((\+|-)\d{2}:\d{2})$/gm, "");
	return isValidDateString(date) && isValidTimeString(time);
}
function isValidDateString(input) {
	if (typeof input !== "string") return false;
	const parts = input.split("-");
	if (parts.length !== 3) return false;
	const [y, m, d] = parts;
	if (!(typeof y === "string" && typeof m === "string" && typeof d === "string")) return false;
	if (!(y.length === 4 && +y > 0)) return false;
	if (!(m.length === 2 && +m > 0 && +m <= 12)) return false;
	if (!(d.length === 2 && +d > 0 && +d <= 31)) return false;
	return true;
}
function isValidTimeString(input) {
	if (typeof input !== "string") return false;
	if (input.match(/(Z)|((\+|-)\d{2}:\d{2})$/gm) !== null) return false;
	const parts = input.split(":");
	if (!((p) => {
		return p.length === 2 || p.length === 3;
	})(parts)) return false;
	const [h, m, _s] = parts;
	if (!(h.length === 2 && +h < 24)) return false;
	if (!(m.length === 2 && +m < 60)) return false;
	if (_s) {
		const [s, ms] = _s.split(".");
		if (!(s.length === 2 && +s < 60)) return false;
		if (ms) {
			if (!(ms.length === 3 && +ms < 1e3)) return false;
		}
	}
	return true;
}
/**
* For a given time, add or subtract minutes, and get the new time and if it falls on the same day, before, or after.
*/
function offsetTime(time, offsetMinutes) {
	if (!isValidTimeString(time)) throw new Error("Invalid time");
	const [h, m] = time.split(":").map((v) => Number(v));
	let newMinutes = h * 60 + m + offsetMinutes;
	const DAY = 1440;
	let dayOffset = 0;
	if (newMinutes < 0) {
		newMinutes = DAY + newMinutes;
		dayOffset = -1;
	} else if (newMinutes >= DAY) {
		newMinutes = newMinutes - DAY;
		dayOffset = 1;
	}
	const min = newMinutes % 60;
	return {
		time: [(newMinutes - min) / 60, min].map((v) => `${v}`.padStart(2, "0")).join(":"),
		dayOffset
	};
}

//#endregion
export { dateTimeStringsToDate, isValidDateString, isValidDateTimeString, isValidTimeString, offsetTime, toDate, toDateString, toTimeString };